import type {
  ClinicalAnatomicalMarkerType,
  ClinicalGeometryType,
  ClinicalLaterality,
  Id,
} from "../../types";
import type {
  AnatomicalTemplate,
  AnatomicalZone,
} from "./anatomicalTemplates";
import {
  buildGeometry,
  inferLaterality,
  validateGeometry,
  type AnatomicalPoint,
} from "./anatomicalGeometry";

export interface PremiumMarkerForm {
  markerType: ClinicalAnatomicalMarkerType;
  geometryType: ClinicalGeometryType;
  administrationId: string;
  zoneCode: string;
  label: string;
  diagnosisText: string;
  treatment: string;
  product: string;
  equipment: string;
  dose: string;
  unit: string;
  plane: string;
  parameters: string;
  notes: string;
}

export interface PremiumDraftMarker {
  marker_key: string;
  marker_type: ClinicalAnatomicalMarkerType;
  geometry_type: ClinicalGeometryType;
  normalized_geometry: Record<string, unknown>;
  administration_id?: Id;
  zone_code?: string;
  laterality: ClinicalLaterality;
  label: string;
  diagnosis_text?: string;
  planned_treatment: Record<string, unknown>;
  clinical_details: Record<string, unknown>;
}

export interface DraftMarkerResult {
  valid: boolean;
  message?: string;
  marker?: PremiumDraftMarker;
}

export const EMPTY_PREMIUM_MARKER_FORM: PremiumMarkerForm = {
  markerType: "TREATMENT",
  geometryType: "POINT",
  administrationId: "",
  zoneCode: "",
  label: "",
  diagnosisText: "",
  treatment: "",
  product: "",
  equipment: "",
  dose: "",
  unit: "",
  plane: "",
  parameters: "",
  notes: "",
};

function optionalText(value: string): string | undefined {
  const normalized = value.trim();
  return normalized || undefined;
}

function optionalNumber(value: string): number | undefined {
  if (!value.trim()) return undefined;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function compactObject(
  value: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).filter(
      ([, item]) =>
        item !== undefined &&
        item !== null &&
        item !== ""
    )
  );
}

export function selectedZone(
  template: AnatomicalTemplate,
  zoneCode: string
): AnatomicalZone | undefined {
  return template.zones.find(
    (zone) => zone.code === zoneCode
  );
}

export function createPremiumDraftMarker({
  template,
  points,
  form,
  markerKey,
}: {
  template: AnatomicalTemplate;
  points: AnatomicalPoint[];
  form: PremiumMarkerForm;
  markerKey: string;
}): DraftMarkerResult {
  const geometry = buildGeometry(
    form.geometryType,
    points
  );

  const validation = validateGeometry(
    form.geometryType,
    geometry
  );

  if (!validation.valid) {
    return validation;
  }

  const zone = selectedZone(
    template,
    form.zoneCode
  );

  if (!zone) {
    return {
      valid: false,
      message:
        "Seleccioná una zona anatómica del catálogo.",
    };
  }

  if (!form.label.trim()) {
    return {
      valid: false,
      message:
        "Ingresá una descripción para la marca.",
    };
  }

  const dose = optionalNumber(form.dose);

  if (form.dose.trim() && dose === undefined) {
    return {
      valid: false,
      message:
        "La dosis debe ser un número válido.",
    };
  }

  return {
    valid: true,
    marker: {
      marker_key: markerKey,
      marker_type: form.markerType,
      geometry_type: form.geometryType,
      normalized_geometry: geometry,
      administration_id:
        form.administrationId || undefined,
      zone_code: zone.code,
      laterality: inferLaterality(
        geometry,
        zone.laterality
      ),
      label: form.label.trim(),
      diagnosis_text:
        optionalText(form.diagnosisText),
      planned_treatment: compactObject({
        treatment:
          optionalText(form.treatment),
        product:
          optionalText(form.product),
        equipment:
          optionalText(form.equipment),
        dose,
        unit:
          optionalText(form.unit),
        plane:
          optionalText(form.plane),
        parameters:
          optionalText(form.parameters),
      }),
      clinical_details: compactObject({
        source:
          "premium_anatomical_editor",
        template_code: template.code,
        template_version:
          template.version,
        template_region:
          template.region,
        template_view: template.view,
        template_sex: template.sex,
        zone_label: zone.label,
        notes:
          optionalText(form.notes),
      }),
    },
  };
}

export function duplicateDraftMarker(
  marker: PremiumDraftMarker,
  markerKey: string
): PremiumDraftMarker {
  return {
    ...marker,
    marker_key: markerKey,
    label: `${marker.label} · copia`,
    normalized_geometry:
      structuredClone(
        marker.normalized_geometry
      ),
    planned_treatment:
      structuredClone(
        marker.planned_treatment
      ),
    clinical_details:
      structuredClone(
        marker.clinical_details
      ),
  };
}

export function markerKeyFor(
  template: AnatomicalTemplate,
  sequence: number
): string {
  return [
    template.code.toLowerCase(),
    Date.now(),
    sequence,
  ].join("-");
}
